import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Plus, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AIQuotaBadge from "@/components/AIQuotaBadge";
import { toast } from "sonner";

function fmtTime(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Chat() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(null);
  const [message, setMessage] = useState("");

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ["chat-sessions"],
    queryFn: async () => (await api.get("/chat/sessions")).data,
  });
  const sessions = useMemo(() => sessionsData?.sessions || [], [sessionsData]);
  useEffect(() => {
    if (!activeId && sessions.length) setActiveId(sessions[0].id);
  }, [activeId, sessions]);

  const { data: chatData, isLoading: chatLoading } = useQuery({
    queryKey: ["chat-session", activeId],
    queryFn: async () => (await api.get(`/chat/sessions/${activeId}`)).data,
    enabled: !!activeId,
    refetchInterval: 30_000,
  });
  const messages = chatData?.messages || [];

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/chat/sessions", { title: "MarketTrace chat" })).data,
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ["chat-sessions"] });
      setActiveId(s.id);
    },
  });

  const sendMut = useMutation({
    mutationFn: async () => (await api.post(`/chat/sessions/${activeId}/messages`, { message })).data,
    onSuccess: () => {
      setMessage("");
      qc.invalidateQueries({ queryKey: ["chat-session", activeId] });
      qc.invalidateQueries({ queryKey: ["chat-sessions"] });
      qc.invalidateQueries({ queryKey: ["ai-quotas"] });
      api.post("/onboarding/progress/asked_chat").catch(() => {});
    },
    onError: (err) => toast.error(err?.response?.data?.detail?.message || "Chat failed"),
  });

  const activeSession = useMemo(() => (sessions || []).find((s) => s.id === activeId), [sessions, activeId]);

  return (
    <div className="space-y-6" data-testid="chat-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Persistent intelligence</div>
          <h1 className="font-heading text-2xl font-bold text-zinc-100">Chat</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Ask follow-up questions against your latest daily brief, watchlists, AI reports, and Trade of the Day context. Every turn is saved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AIQuotaBadge endpoint="chat_assistant" compact />
          <Button onClick={() => createMut.mutate()} disabled={createMut.isPending} className="h-8 text-xs bg-zinc-100 text-zinc-950 hover:bg-white" data-testid="btn-new-chat">
            <Plus size={12} className="mr-1" /> New chat
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 min-h-[620px]">
        <aside className="rounded-md border border-zinc-800 bg-zinc-950/50 p-2 space-y-1 max-h-[620px] overflow-auto">
          {sessionsLoading && <div className="p-3 text-xs text-zinc-500">Loading chats…</div>}
          {!sessionsLoading && sessions.length === 0 && (
            <button onClick={() => createMut.mutate()} className="w-full rounded border border-dashed border-zinc-800 p-4 text-xs text-zinc-500 hover:text-zinc-200" data-testid="empty-new-chat">
              Start your first saved chat
            </button>
          )}
          {sessions.map((s) => (
            <button key={s.id} onClick={() => setActiveId(s.id)} className={`w-full rounded px-3 py-2 text-left text-xs ${activeId === s.id ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"}`} data-testid={`chat-session-${s.id}`}>
              <div className="flex items-center gap-2"><MessageSquare size={12} /> <span className="truncate">{s.title || "MarketTrace chat"}</span></div>
              <div className="mt-1 text-[10px] text-zinc-600">{fmtTime(s.updated_at)}</div>
            </button>
          ))}
        </aside>

        <section className="rounded-md border border-zinc-800 bg-zinc-950/50 flex flex-col min-h-[620px]">
          <div className="border-b border-zinc-800 px-4 py-3">
            <div className="font-heading text-sm text-zinc-100">{activeSession?.title || "MarketTrace chat"}</div>
            <div className="text-[11px] text-zinc-500">Grounded in latest MarketTrace intelligence history.</div>
          </div>
          <div className="flex-1 overflow-auto p-4 space-y-3" data-testid="chat-messages">
            {chatLoading && <div className="text-xs text-zinc-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Loading messages…</div>}
            {!activeId && !chatLoading && <div className="text-xs text-zinc-500">Create a chat to start asking questions.</div>}
            {activeId && messages.length === 0 && !chatLoading && (
              <div className="rounded-md border border-zinc-800 p-4 text-xs text-zinc-500">
                Try: “What should I look at first today?” or “Which watchlist item changed most?”
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`rounded-md p-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "ml-auto max-w-[85%] bg-zinc-800 text-zinc-100" : "mr-auto max-w-[90%] bg-zinc-900 text-zinc-200 border border-zinc-800"}`} data-testid={`chat-message-${m.role}`}>
                <div className="mb-1 text-[10px] uppercase tracking-widest text-zinc-500">{m.role}</div>
                {m.content}
              </div>
            ))}
            {sendMut.isPending && <div className="text-xs text-zinc-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Thinking…</div>}
          </div>
          <form className="border-t border-zinc-800 p-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (activeId && message.trim() && !sendMut.isPending) sendMut.mutate(); }}>
            <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ask about signals, watchlists, daily brief, or history…" disabled={!activeId || sendMut.isPending} className="bg-zinc-900 border-zinc-800" data-testid="input-chat-message" />
            <Button type="submit" disabled={!activeId || !message.trim() || sendMut.isPending} className="bg-zinc-100 text-zinc-950 hover:bg-white" data-testid="btn-send-chat">
              {sendMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </form>
        </section>
      </div>
    </div>
  );
}
